// ObjFieldListMulti.js
import React, {
  useMemo,
  useCallback,
  useState,
  useEffect,
  useRef,
} from 'react';
import { isValidColor, lighten } from '../../../Utils/colorManipulator';

const ObjFieldListMulti = ({
  keyParam,
  attrName,
  readOnly,
  options,
  values, // string[]
  unit,
  onChangeValues, // (newValues: string[]) => void
  isModal = false, // New parameter
  // options de style facultatives
  selectedBg = 'var(--ds-bg-secondary)',
  unselectedBg = 'var(--bg-base)',
  selectedBorder = 'var(--ds-border-intense)',
  unselectedBorder = 'var(--ds-border-base)',
  hoverBg = 'var(--ds-bg-secondary)',
  textColor = 'inherit',
  modalTitle = 'Select Tags',
}) => {
  // State for pending changes when in modal mode
  const [pendingValues, setPendingValues] = useState(values || []);
  const [hasChanges, setHasChanges] = useState(false);

  //  Suivre si le composant est "actif" (en cours d'édition)
  const [isEditingActive, setIsEditingActive] = useState(false);

  // Référence pour la valeur initiale au moment de l'ouverture du modal
  const initialValuesRef = useRef(values || []);

  //  Synchroniser seulement si pas en cours d'édition
  useEffect(() => {
    if (!isModal) {
      // Mode direct : toujours synchroniser
      setPendingValues(values || []);
      setHasChanges(false);
    } else if (!isEditingActive) {
      setPendingValues(values || []);
      initialValuesRef.current = values || [];
      setHasChanges(false);
    }
  }, [values, isModal, isEditingActive]);

  //  Marquer comme "actif" dès qu'on commence à éditer
  useEffect(() => {
    if (isModal && !isEditingActive) {
      setIsEditingActive(true);
    }
  }, [isModal]);

  const arrOptions = useMemo(() => {
    try {
      return Array.isArray(options) ? options : JSON.parse(options || '[]');
    } catch (e) {
      console.error('Invalid options for ObjFieldListMulti:', e);
      return [];
    }
  }, [options]);

  const isChecked = useCallback(
    (val) => {
      const valuesToCheck = isModal ? pendingValues : values || [];
      return valuesToCheck.includes(val);
    },
    [isModal, pendingValues, values]
  );

  const toggleValue = useCallback(
    (val) => {
      if (readOnly) return;

      if (isModal) {
        // In modal mode, update pending values
        const set = new Set(pendingValues || []);
        set.has(val) ? set.delete(val) : set.add(val);
        const newPendingValues = Array.from(set);
        setPendingValues(newPendingValues);

        // Check if there are changes
        const originalSet = new Set(values || []);
        const pendingSet = new Set(newPendingValues);
        const hasChanges =
          originalSet.size !== pendingSet.size ||
          !Array.from(originalSet).every((v) => pendingSet.has(v));
        setHasChanges(hasChanges);
      } else {
        // Direct mode, update immediately
        const set = new Set(values || []);
        set.has(val) ? set.delete(val) : set.add(val);
        onChangeValues(Array.from(set));
      }
    },
    [readOnly, isModal, pendingValues, values, onChangeValues]
  );

  const handleConfirm = useCallback(() => {
    if (isModal && hasChanges) {
      onChangeValues(pendingValues);
      setHasChanges(false);
    }
  }, [isModal, hasChanges, pendingValues, onChangeValues]);

  const handleCancel = useCallback(() => {
    if (isModal) {
      setPendingValues(values || []);
      setHasChanges(false);
    }
  }, [isModal, values]);

  const containerStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    padding: '8px 16px',
    fontSize: '11px',
  };

  const optionsContainerStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    alignItems: 'flex-start',
  };

  const buttonsContainerStyle = {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    justifyContent: 'center',
    margin: 0,
    width: '100%',
  };

  const titleStyle = {};

  const buttonStyle = (variant = 'primary') => ({
    padding: '2px 6px',
    borderRadius: 4,
    border: `1px solid var(--ds-border-intense)`,
    background:
      variant === 'primary' ? 'var(--ds-bg-secondary)' : 'var(--ds-bg-base)',
    color:
      variant === 'primary'
        ? 'var(--ds-text-base)'
        : 'var(--ds-text-secondary)',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: variant === 'primary' ? '500' : 'normal',
    transition: 'all 120ms ease',
  });

  const itemStyle = (opt, checked) => {
    let lgColor;
    if (isValidColor(opt.color)) {
      lgColor = lighten(opt.color, 0.9);
    }
    return {
      display: 'inline-flex',
      alignItems: 'center',
      boxShadow: '0 0 0 1px rgba(0,0,0,.04), 0 1px 2px rgba(0,0,0,.08)',
      gap: 8,
      padding: '4px 8px',
      borderRadius: 4,
      border: checked ? '0px' : '1px solid var(--ds-border-base)',
      background: checked ? lgColor || selectedBg : unselectedBg,
      color: textColor,
      cursor: readOnly ? 'default' : 'pointer',
      userSelect: 'none',
      transition:
        'background 120ms ease, border-color 120ms ease, transform 60ms',
    };
  };

  const iconStyle = (opt, checked) => ({
    width: 16,
    height: 16,
    backgroundColor: checked
      ? opt.color || 'var(--ds-icon-base)'
      : 'var(--ds-icon-base)',
    mask: opt.icon ? `url('${opt.icon}')` : undefined,
    WebkitMask: opt.icon ? `url('${opt.icon}')` : undefined,
    maskRepeat: 'no-repeat',
    WebkitMaskRepeat: 'no-repeat',
    maskSize: '16px 16px',
    WebkitMaskSize: '16px 16px',
    opacity: checked ? 1 : 0.5,
    flex: '0 0 16px',
  });

  // input natif invisible mais accessible
  const srOnlyInput = {
    position: 'absolute',
    opacity: 0,
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
    border: 0,
  };

  return (
    <div style={containerStyle}>
      {/* Modal title - only shown in modal mode */}
      {isModal && modalTitle && <div style={titleStyle}>{modalTitle}</div>}

      <div style={optionsContainerStyle}>
        {arrOptions.map((opt) => {
          const checked = isChecked(opt.value);
          const id = `${keyParam}${attrName}-${opt.value}`;
          console.log('id:', id);
          return (
            <label
              key={id}
              htmlFor={id}
              style={itemStyle(opt, checked)}
              onMouseEnter={(e) => {
                if (!readOnly && !checked)
                  e.currentTarget.style.background = hoverBg;
              }}
              onMouseLeave={(e) => {
                if (!readOnly && !checked)
                  e.currentTarget.style.background = unselectedBg;
              }}
              onMouseDown={(e) => {
                if (!readOnly) e.currentTarget.style.transform = 'scale(0.98)';
              }}
              onMouseUp={(e) => {
                if (!readOnly) e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <input
                id={id}
                type="checkbox"
                checked={checked}
                onChange={() => toggleValue(opt.value)}
                disabled={readOnly}
                style={srOnlyInput}
              />

              {opt.icon ? <div style={iconStyle(opt, checked)} /> : null}

              <span
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {opt.display}
                {unit || ''}
              </span>
            </label>
          );
        })}
      </div>

      {/* Confirm/Cancel buttons - only shown in modal mode and when there are changes */}
      {isModal && hasChanges && !readOnly && (
        <div style={buttonsContainerStyle}>
          <button
            type="button"
            style={buttonStyle('primary')}
            onClick={handleConfirm}
            onMouseEnter={(e) => {
              e.currentTarget.style.background =
                'var(--ds-bg-accent-hover, var(--ds-bg-accent))';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'var(--ds-bg-accent)';
            }}
          >
            Confirm
          </button>
          <button
            type="button"
            style={buttonStyle('secondary')}
            onClick={handleCancel}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = hoverBg;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = unselectedBg;
            }}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

export default ObjFieldListMulti;
